<script setup>
import { computed } from 'vue';
/**
 * Overlay Badge
 *
 * Wraps arbitrary content (via default slot) and conditionally renders a small badge
 * positioned relative to the slotted content. The badge can display either a numeric
 * counter (clamped to `max+`) or a custom string value (shown as-is).
 *
 * Common use cases:
 * - Notification count on an icon
 * - Unread items indicator on a button
 * - Small status label overlay
 *
 * Rendering rules:
 * - If `showZero` is true, the badge always renders (even if `value` is 0).
 * - If `value` is a string, the badge renders only when the string length > 0.
 * - If `value` is numeric (or numeric-like), the badge renders only when numeric value > 0.
 *
 * Animation rules:
 * - `pulse` applies a pulsing animation to the badge.
 * - `ringPulse` changes the DOM structure to support a "ping ring" effect.
 *   When `ringPulse` is true and `pulse` is true, a ping animation is rendered behind the badge.
 */
/**
 * Component props for BadgeCounter.
 */
const props = defineProps({
	/**
	 * @property {number|string} [value=0]
	 *   The badge content input. If a number (or numeric-like string), it is coerced to a number
	 *   and rendered as a counter. If a non-numeric string, it is rendered verbatim.
	 */
	value: { type: [Number, String], default: 0 },
	/**
	 * @property {number} [max=9]
	 *   Maximum numeric value displayed before clamping to a "{max}+" string (e.g., "9+").
	 *   Only applies when `value` is numeric.
	 */
	max: { type: Number, default: 9 },
	/**
	 * @property {boolean} [showZero=false]
	 *   When true, forces the badge to render even when the numeric value is 0.
	 */
	showZero: { type: Boolean, default: false },
	/**
	 * @property {boolean} [pulse=false]
	 *   When true, applies a pulsing animation. If `ringPulse` is true, this enables the ping ring.
	 */
	pulse: { type: Boolean, default: false },
	/**
	 * @property {boolean} [ringPulse=false]
	 *   When true, uses a "ring/ping" style animation wrapper (when `pulse` is also true).
	 */
	ringPulse: { type: Boolean, default: false },
	/**
	 * @property {string} [positionClass="-bottom-1 -right-1"]
	 *   Tailwind (or other) class string applied to the badge container to position it.
	 *   Example: "-top-1 -left-1", "top-0 right-0", etc.
	 */
	positionClass: { type: String, default: '-bottom-1 -right-1' }
});

/**
 * Numeric interpretation of `props.value`.
 * Non-finite conversions become 0 to keep rendering predictable.
 *
 * @type {import('vue').ComputedRef<number>}
 */
const numericValue = computed(() => {
	const n = Number(props.value);
	return Number.isFinite(n) ? n : 0;
});

/**
 * Whether the badge should be rendered.
 * - `showZero` forces true
 * - string value renders if non-empty
 * - numeric value renders if > 0
 *
 * @type {import('vue').ComputedRef<boolean>}
 */
const show = computed(() => {
	if (props.showZero) return true;
	if (typeof props.value === 'string') return props.value.length > 0;
	return numericValue.value > 0;
});

/**
 * Final text displayed inside the badge.
 * - string value returns as-is
 * - numeric value clamps to "{max}+" if it exceeds `max`
 *
 * @type {import('vue').ComputedRef<string>}
 */
const displayValue = computed(() => {
	if (typeof props.value === 'string') return props.value;
	if (numericValue.value > props.max) return `${props.max}+`;
	return `${numericValue.value}`;
});
</script>

<template>
	<div class="relative inline-flex overflow-visible isolate">
		<slot />
		<span
			v-if="show && !ringPulse"
			class="absolute z-[9999] flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-[#f97316] text-white text-[0.65rem] leading-none pointer-events-none"
			:class="[positionClass, pulse ? 'animate-pulse' : '']"
		>
			{{ displayValue }}
		</span>

		<span v-else-if="show && ringPulse" class="absolute z-[9999] pointer-events-none" :class="positionClass">
			<span v-if="pulse" class="absolute inset-0 rounded-full bg-[#f97316] opacity-60 animate-ping" />
			<span class="relative flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-[#f97316] text-white text-[0.65rem] leading-none">
				{{ displayValue }}
			</span>
		</span>
	</div>
</template>
